import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';
import { de, enUS } from 'date-fns/locale';
import { Clock, History, ShieldCheck, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from './ui/dialog';
import UserAvatar from './UserAvatar';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

// Convert ISO string -> "YYYY-MM-DDTHH:mm" for <input type="datetime-local">
const toLocalInput = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
};

const toISO = (localValue) => {
  if (!localValue) return null;
  const d = new Date(localValue);
  if (Number.isNaN(d.getTime())) return null;
  return d.toISOString();
};

const formatDuration = (seconds) => {
  if (!seconds || seconds < 0) return '0h 0m';
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  return `${h}h ${m}m`;
};

// Inner form — state initialised from props (no setState-in-effect).
// Parent re-mounts this via `key={entry.id}` so a new entry resets all fields cleanly.
const EditForm = ({ entry, audit, onCancel, onSaved }) => {
  const { getAuthHeader, user } = useAuth();
  const { t, i18n } = useTranslation();
  const [startTime, setStartTime] = useState(() => toLocalInput(entry.start_time));
  const [endTime, setEndTime] = useState(() => toLocalInput(entry.end_time || entry.paused_at || new Date().toISOString()));
  const [description, setDescription] = useState(() => entry.description || '');
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);

  const getLocale = () => (i18n.language === 'de' ? de : enUS);

  const handleSave = async () => {
    const startISO = toISO(startTime);
    const endISO = toISO(endTime);
    if (!startISO) {
      toast.error(t('adminEdit.invalidStart'));
      return;
    }
    if (!endISO || new Date(endISO) <= new Date(startISO) || !previewSeconds) {
      toast.error(t('adminEdit.endBeforeStart'));
      return;
    }
    setSaving(true);
    try {
      const payload = {
        start_time: startISO,
        end_time: endISO,
        description: description || '',
        reason: reason || null,
        duration: previewSeconds,
        date: startTime.slice(0, 10),
      };
      const endpoint = user?.role === 'admin' ? `admin/time-entries/${entry.id}` : `time-entries/${entry.id}/edit`;
      await axios.put(`${API_URL}/${endpoint}`, payload, {
        headers: getAuthHeader(),
      });
      toast.success(t('adminEdit.saved'));
      onSaved && onSaved();
    } catch (err) {
      toast.error(t('adminEdit.error'));
    } finally {
      setSaving(false);
    }
  };

  let previewSeconds = null;
  const sISO = toISO(startTime);
  const eISO = toISO(endTime);
  if (sISO && eISO) {
    const diff = (new Date(eISO) - new Date(sISO)) / 1000 - (entry.total_pause_duration || 0);
    previewSeconds = Math.max(0, Math.floor(diff));
  }

  const changeDuration = (unit, value) => {
    if (!sISO) return;
    const seconds = previewSeconds || 0;
    const parts = { hours: Math.floor(seconds / 3600), minutes: Math.floor(seconds % 3600 / 60), seconds: seconds % 60 };
    parts[unit] = Math.max(0, Math.floor(Number(value) || 0));
    const total = parts.hours * 3600 + parts.minutes * 60 + parts.seconds;
    setEndTime(toLocalInput(new Date(new Date(sISO).getTime() + (total + (entry.total_pause_duration || 0)) * 1000).toISOString()));
  };

  return (
    <>
      {/* User info */}
      <div className="flex items-center gap-3 bg-zinc-50 rounded-xl px-3 py-2" data-testid="edit-time-entry-owner">
        <UserAvatar user={{ id: entry.user_id, name: entry.user_name }} size="md" />
        <div className="text-sm">
          <div className="font-semibold text-zinc-900">{entry.user_name}</div>
          <div className="text-xs text-zinc-500">{entry.date}</div>
        </div>
        {entry.edited_by_admin && (
          <span className="ml-auto text-[10px] uppercase tracking-wide font-bold bg-[#FEF3C7] text-[#B45309] px-2 py-0.5 rounded-full inline-flex items-center gap-1">
            <ShieldCheck className="w-3 h-3" />
            {t('adminEdit.editedBadge')}
          </span>
        )}
      </div>

      <div className="space-y-4 py-2">
        {!entry.end_time && <p className="text-sm text-amber-800 bg-amber-50 p-3 rounded-lg" data-testid="edit-active-timer-warning">{t('timeCorrection.activeWarning')}</p>}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <Label className="text-sm font-medium">{t('adminEdit.start')}</Label>
            <Input
              data-testid="admin-edit-start-input"
              type="datetime-local"
              step="1"
              value={startTime}
              onChange={(e) => setStartTime(e.target.value)}
              className="mt-1.5 h-10"
            />
          </div>
          <div>
            <Label className="text-sm font-medium">{t('adminEdit.end')}</Label>
            <Input
              data-testid="admin-edit-end-input"
              type="datetime-local"
              step="1"
              value={endTime}
              onChange={(e) => setEndTime(e.target.value)}
              className="mt-1.5 h-10"
            />
          </div>
        </div>

        <div>
          <Label>{t('timer.manualDurationLabel')}</Label>
          <div className="grid grid-cols-3 gap-3 mt-1.5">
            {['hours', 'minutes', 'seconds'].map((unit, index) => (
              <div key={unit} className="min-w-0">
                <Input type="number" min="0" max={index ? 59 : undefined} step="1" inputMode="numeric"
                  aria-label={t(`timeCorrection.${unit}`)} data-testid={`edit-duration-${unit}`}
                  value={index === 0 ? Math.floor((previewSeconds || 0) / 3600) : index === 1 ? Math.floor((previewSeconds || 0) % 3600 / 60) : (previewSeconds || 0) % 60}
                  onChange={event => changeDuration(unit, event.target.value)} />
                <span className="text-xs text-zinc-500">{t(`timeCorrection.${unit}`)}</span>
              </div>
            ))}
          </div>
        </div>

        {previewSeconds !== null && (
          <div className="text-xs text-zinc-600 bg-zinc-50 rounded-lg px-3 py-2" data-testid="edit-duration-preview">
            <span className="font-medium">{t('adminEdit.newDuration')}: </span>
            <span className="font-bold text-[#D93025]">{formatDuration(previewSeconds)}</span>
            {entry.total_pause_duration ? (
              <span className="ml-2 text-zinc-500">
                ({t('adminEdit.minusPause')}: {formatDuration(entry.total_pause_duration)})
              </span>
            ) : null}
          </div>
        )}

        <div>
          <Label className="text-sm font-medium">{t('adminEdit.description')}</Label>
          <Textarea
            data-testid="admin-edit-description-input"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder={t('adminEdit.descriptionPlaceholder')}
            className="mt-1.5 resize-none"
            rows={3}
          />
        </div>

        <div>
          <Label className="text-sm font-medium flex items-center gap-2">
            <AlertCircle className="w-4 h-4 text-zinc-500" />
            {t('adminEdit.reason')}
          </Label>
          <Input
            data-testid="admin-edit-reason-input"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder={t('adminEdit.reasonPlaceholder')}
            className="mt-1.5 h-10"
          />
          <p className="text-xs text-zinc-500 mt-1">{t('adminEdit.reasonHint')}</p>
        </div>

        {audit.length > 0 && (
          <div className="border-t pt-4">
            <Label className="text-sm font-medium mb-2 flex items-center gap-2">
              <History className="w-4 h-4 text-zinc-500" />
              {t('adminEdit.auditTitle')} ({audit.length})
            </Label>
            <div className="space-y-2 max-h-44 overflow-y-auto">
              {audit.map((a) => (
                <div key={a.id} className="bg-zinc-50 rounded-lg px-3 py-2 text-xs" data-testid={`audit-row-${a.id}`}>
                  <div className="flex items-center justify-between mb-1">
                    <span className="font-semibold text-zinc-800">{a.actor_name || a.admin_name}</span>
                    <span className="text-zinc-500">
                      {format(new Date(a.changed_at), 'd. MMM yyyy, HH:mm', { locale: getLocale() })}
                    </span>
                  </div>
                  {a.reason && <div className="text-zinc-700 italic mb-1">&ldquo;{a.reason}&rdquo;</div>}
                  <div className="grid grid-cols-2 gap-2 text-zinc-600">
                    <div>
                      <div className="text-zinc-400">{t('adminEdit.before')}</div>
                      <div>⏱ {formatDuration(a.before?.duration)}</div>
                    </div>
                    <div>
                      <div className="text-zinc-400">{t('adminEdit.after')}</div>
                      <div className="text-zinc-800 font-medium">⏱ {formatDuration(a.after?.duration)}</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      <DialogFooter className="gap-2 pt-2">
        <Button data-testid="cancel-edit-time-entry" variant="outline" onClick={onCancel} disabled={saving} className="rounded-full px-6">
          {t('common.cancel')}
        </Button>
        <Button
          data-testid="admin-edit-save-btn"
          onClick={handleSave}
          disabled={saving}
          className="bg-[#D93025] hover:bg-[#B91C1C] text-white rounded-full px-6"
        >
          {saving ? t('common.loading') : t('common.save')}
        </Button>
      </DialogFooter>
    </>
  );
};

const AdminTimeEntryEditDialog = ({ open, onOpenChange, entry, onSaved }) => {
  const { getAuthHeader, user } = useAuth();
  const { t } = useTranslation();
  const [audit, setAudit] = useState([]);

  useEffect(() => {
    if (!entry || !open || user?.role !== 'admin') return;
    setAudit([]);
    let cancelled = false;
    axios
      .get(`${API_URL}/admin/time-entries/${entry.id}/audit`, { headers: getAuthHeader() })
      .then(res => { if (!cancelled) setAudit(res.data || []); })
      .catch(error => { console.error('Failed to load time entry history:', error); if (!cancelled) setAudit([]); });
    return () => { cancelled = true; };
  }, [entry, open, getAuthHeader, user?.role]);

  if (!entry) return null;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="edit-time-entry-dialog" className="max-w-[calc(100%-2rem)] sm:max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#D93025] to-[#B91C1C] flex items-center justify-center text-white">
              <Clock className="w-5 h-5" />
            </div>
            {t('adminEdit.title')}
          </DialogTitle>
          <DialogDescription className="sr-only">{t('adminEdit.title')}</DialogDescription>
        </DialogHeader>
        <EditForm
          key={entry.id}
          entry={entry}
          audit={audit}
          onCancel={() => onOpenChange(false)}
          onSaved={() => { onSaved && onSaved(); onOpenChange(false); }}
        />
      </DialogContent>
    </Dialog>
  );
};

export default AdminTimeEntryEditDialog;
